import Api from '@/api'

export default {
  namespaced: true,

  state: {
    jobs: [],
    myJobs: [],
  },

  getters: {
    jobs(state) {
      return state.jobs
    },

    myJobs(state) {
      return state.myJobs
    },

    jobsByWorkerAndDate(state) {
      const map = {}
      state.jobs.forEach((job) => {
        ;(job.workers || []).forEach((w) => {
          const key = `${w.id}_${job.date}`
          if (!map[key]) map[key] = []
          map[key].push(job)
        })
      })
      return map
    },
  },

  mutations: {
    setJobs(state, jobs) {
      state.jobs = jobs
    },
    setMyJobs(state, jobs) {
      state.myJobs = jobs
    },
    // adjustJobSeries can return a job that was reopened rather than
    // created (e.g. re-adding a weekday whose prior occurrence was
    // cancelled, not deleted) — that id is already in state.jobs, so a
    // blind push() would duplicate it and trip Vue's :key warning.
    addJobs(state, jobs) {
      jobs.forEach((job) => {
        const i = state.jobs.findIndex((j) => j.id === job.id)
        if (i !== -1) {
          state.jobs.splice(i, 1, job)
        } else {
          state.jobs.push(job)
        }
      })
    },
    updateJobInList(state, job) {
      const i = state.jobs.findIndex((j) => j.id === job.id)
      if (i !== -1) state.jobs.splice(i, 1, job)
    },
    removeJob(state, id) {
      state.jobs = state.jobs.filter((j) => j.id !== id)
    },
    removeJobsBySeriesId(state, recurrenceGroupId) {
      state.jobs = state.jobs.filter(
        (j) => j.recurrence_group_id !== recurrenceGroupId,
      )
    },
    completeJobsBySeriesId(state, recurrenceGroupId) {
      state.jobs = state.jobs.map((j) =>
        j.recurrence_group_id === recurrenceGroupId
          ? { ...j, status: 'completed' }
          : j,
      )
    },
    cancelJobsBySeriesId(state, recurrenceGroupId) {
      state.jobs = state.jobs.map((j) =>
        j.recurrence_group_id === recurrenceGroupId
          ? { ...j, status: 'cancelled' }
          : j,
      )
    },
    // Day-removal only cancels the specific jobs matching the removed
    // weekday, not the whole series — unlike cancelJobsBySeriesId.
    cancelJobsByIds(state, ids) {
      if (!ids || !ids.length) return
      state.jobs = state.jobs.map((j) =>
        ids.includes(j.id) ? { ...j, status: 'cancelled' } : j,
      )
    },
    reopenJobsBySeriesId(state, recurrenceGroupId) {
      state.jobs = state.jobs.map((j) =>
        j.recurrence_group_id === recurrenceGroupId && j.status === 'cancelled'
          ? { ...j, status: 'upcoming' }
          : j,
      )
    },
  },

  actions: {
    async getJobs({ commit }, params = {}) {
      const res = await Api.get('/admin/schedule/jobs', { params })
      const jobs = res.data.data || []
      commit('setJobs', jobs)
      return jobs
    },

    // Tradie-facing — only jobs the authed user is assigned to as a worker
    // (ScheduleJobController@index). Unlike getJobs, this needs no
    // schedule.view permission; any authed tradie can call it.
    async getMyJobs({ commit }, params = {}) {
      const res = await Api.get('/schedule/jobs', { params })
      const jobs = res.data.data || []
      commit('setMyJobs', jobs)
      return jobs
    },

    async createJob({ commit }, payload) {
      const res = await Api.post('/admin/schedule/jobs', payload)
      const jobs = res.data.data.jobs || []
      commit('addJobs', jobs)
      return jobs
    },

    async updateJob({ commit }, { id, ...fields }) {
      const res = await Api.put(`/admin/schedule/jobs/${id}`, fields)
      const job = res.data.data
      commit('updateJobInList', job)
      return job
    },

    // force/confirmed let a caller retry past a TIMESHEET_CONFLICT (409) —
    // confirmed is the exact conflicts[] the user was shown and agreed to
    // delete past. Sent as query params, not a body: this repo is pinned to
    // axios 0.19, where axios.delete(url, { data }) is an easy-to-miss
    // gotcha, so force-delete avoids it entirely.
    async deleteJob({ commit }, { id, force = false, confirmed = null } = {}) {
      const params = {}
      if (force) params.force = true
      if (confirmed) params.confirmed = JSON.stringify(confirmed)
      await Api.delete(`/admin/schedule/jobs/${id}`, { params })
      commit('removeJob', id)
    },

    async deleteJobSeries(
      { commit },
      { recurrenceGroupId, force = false, confirmed = null } = {},
    ) {
      const params = {}
      if (force) params.force = true
      if (confirmed) params.confirmed = JSON.stringify(confirmed)
      const res = await Api.delete(
        `/admin/schedule/jobs/series/${recurrenceGroupId}`,
        { params },
      )
      commit('removeJobsBySeriesId', recurrenceGroupId)
      return res.data.data
    },

    async completeJobSeries({ commit }, recurrenceGroupId) {
      const res = await Api.put(
        `/admin/schedule/jobs/series/${recurrenceGroupId}/complete`,
      )
      const data = res.data.data
      if (data.updated > 0) {
        commit('completeJobsBySeriesId', recurrenceGroupId)
      }
      return data
    },

    async cancelJobSeries({ commit }, recurrenceGroupId) {
      const res = await Api.put(
        `/admin/schedule/jobs/series/${recurrenceGroupId}/cancel`,
      )
      const data = res.data.data
      if (data.updated > 0) {
        commit('cancelJobsBySeriesId', recurrenceGroupId)
      }
      return data
    },

    async reopenJobSeries({ commit }, recurrenceGroupId) {
      const res = await Api.put(
        `/admin/schedule/jobs/series/${recurrenceGroupId}/reopen`,
      )
      const data = res.data.data
      if (data.updated > 0) {
        commit('reopenJobsBySeriesId', recurrenceGroupId)
      }
      return data
    },

    // repeatStartDate must be on/before the series' current start,
    // repeatEndDate must be after its current end — both additive-only,
    // server-validated. repeatDays is the full new day set and can both add
    // AND drop days relative to the series' current pattern; a drop is
    // gated server-side on a timesheet-conflict check (see the 409 handling
    // at the call site) and, if clear, cancels the matching jobs.
    async adjustJobSeries(
      { commit },
      {
        recurrenceGroupId,
        repeatStartDate,
        repeatEndDate,
        repeatDays,
        force = false,
        confirmed = null,
      },
    ) {
      const payload = {}
      if (repeatStartDate) payload.repeat_start_date = repeatStartDate
      if (repeatEndDate) payload.repeat_end_date = repeatEndDate
      if (repeatDays) payload.repeat_days = repeatDays
      if (force) payload.force = true
      if (confirmed) payload.confirmed = confirmed

      const res = await Api.put(
        `/admin/schedule/jobs/series/${recurrenceGroupId}/adjust`,
        payload,
      )
      const jobs = res.data.data.jobs || []
      const noOpFields = res.data.data.no_op_fields || []
      const cancelledJobIds = res.data.data.cancelled_job_ids || []
      commit('addJobs', jobs)
      commit('cancelJobsByIds', cancelledJobIds)
      return { jobs, noOpFields, cancelledJobIds }
    },

    async syncJobWorkers({ commit }, { id, workerIds, applyScope = 'this' }) {
      const res = await Api.put(`/admin/schedule/jobs/${id}/workers`, {
        worker_ids: workerIds,
        apply_scope: applyScope,
      })
      const job = res.data.data
      commit('updateJobInList', job)
      return job
    },

    async getJobSeries(_, recurrenceGroupId) {
      const res = await Api.get(
        `/admin/schedule/jobs/series/${recurrenceGroupId}`,
      )
      return res.data.data
    },

    async detachJobFromSeries({ commit }, id) {
      const res = await Api.put(`/admin/schedule/jobs/${id}/detach-recurrence`)
      const job = res.data.data
      commit('updateJobInList', job)
      return job
    },

    async unsetSeries({ commit, state }, recurrenceGroupId) {
      const res = await Api.put(
        `/admin/schedule/jobs/series/${recurrenceGroupId}/unset`,
      )
      state.jobs
        .filter((j) => j.recurrence_group_id === recurrenceGroupId)
        .forEach((j) =>
          commit('updateJobInList', { ...j, recurrence_group_id: null }),
        )
      return res.data.data
    },

    async getAvailableWorkers(_, params = {}) {
      const res = await Api.get('/admin/schedule/workers', { params })
      return res.data.data || []
    },

    async syncJobSupervisors(
      { commit },
      { id, supervisorIds, applyScope = 'this' },
    ) {
      const res = await Api.put(`/admin/schedule/jobs/${id}/supervisors`, {
        supervisor_ids: supervisorIds,
        apply_scope: applyScope,
      })
      const job = res.data.data
      commit('updateJobInList', job)
      return job
    },

    async getAvailableSupervisors(_, params = {}) {
      const res = await Api.get('/admin/schedule/supervisors', { params })
      return res.data.data || []
    },
  },
}
